import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useYearTransition } from "../../hooks/useYearTransition";
import { TransitionStage } from "../../types";
import { YearTransitionPromote } from "./promote/YearTransitionPromote";

const STEP_LABELS = ["Promote", "Create Year", "Close Year", "Fee Structure", "Initialize", "Activate"];

// The whole flow is driven by the backend's `stage` field (see the Year
// Transition API design) — this component just renders whatever the current
// stage says, with no client-side derivation of "is this step done."
// yearTransitionService.ts mocks that backend until it's built.
const YearTransitionWizard: React.FC = () => {
  const { response, loading, acting, error, actions } = useYearTransition();
  const [feeEdits, setFeeEdits] = useState<Record<string, number>>({});

  const handleSubmitFees = () => {
    const feeStructure = response?.data.feeStructure ?? [];
    actions.submitFees(feeStructure.map((f) => ({ name: f.name, newFee: feeEdits[f.name] ?? f.currentFee })));
  };

  const renderStageContent = () => {
    if (!response) return null;

    switch (response.stage) {
      case TransitionStage.PROMOTE:
        return (
          <YearTransitionPromote
            grade={response.grade ?? ""}
            section={response.section ?? ""}
            message={response.message}
            students={response.data.students ?? []}
            acting={acting}
            onPromote={(promotions) => actions.promote(response.grade ?? "", response.section ?? "", promotions)}
          />
        );

      case TransitionStage.YEAR_CREATED:
      case TransitionStage.YEAR_CLOSED:
      case TransitionStage.INITIALIZED:
        return (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {response.message}
            </Alert>
            <Button variant="contained" disabled={acting} onClick={actions.continueStage}>
              {acting ? "Working..." : "Continue"}
            </Button>
          </>
        );

      case TransitionStage.FEE_STRUCTURE: {
        const feeStructure = response.data.feeStructure ?? [];
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {response.message}
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mb: 2, maxWidth: 480, borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, letterSpacing: 0.3, color: "text.secondary", py: 0.75 }}>GRADE</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, letterSpacing: 0.3, color: "text.secondary", py: 0.75 }} align="right">
                      CURRENT
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, letterSpacing: 0.3, color: "text.secondary", py: 0.75 }} align="right">
                      NEW FEE
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeStructure.map((f) => (
                    <TableRow key={f.name} hover sx={{ "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ py: 0.5, fontSize: 13 }}>{f.name}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: 13, color: "text.secondary" }} align="right">
                        {f.currentFee}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={feeEdits[f.name] ?? f.currentFee}
                          onChange={(e) => setFeeEdits((prev) => ({ ...prev, [f.name]: Number(e.target.value) }))}
                          inputProps={{ onWheel: (e) => (e.target as HTMLElement).blur(), sx: { textAlign: "right", py: 0.5, fontSize: 13 } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button variant="contained" disabled={acting} onClick={handleSubmitFees}>
              {acting ? "Submitting..." : "Submit Fee Structure"}
            </Button>
          </>
        );
      }

      case TransitionStage.COMPLETE:
        return (
          <Alert severity="success">
            {response.message}
          </Alert>
        );

      default:
        return null;
    }
  };

  const activeStep = response ? response.stage - 1 : 0;

  return (
    <Box sx={{ ml: 5, mt: 2, mr: 5 }}>
      <Typography variant="h5" fontWeight={600} mb={1}>
        Year Transition
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Move the school from the current academic year to a new one. Progress is tracked by the server — each step unlocks as the previous one
        completes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {response && response.stage !== TransitionStage.PROMOTE && response.stage <= STEP_LABELS.length && (
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              {STEP_LABELS[response.stage - 1]}
            </Typography>
          )}
          {renderStageContent()}
        </Paper>
      )}
    </Box>
  );
};

export default YearTransitionWizard;
